import { onBeforeUnmount, onMounted, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue'

/**
 * Sets textarea height to fit full content (no inner scrollbar).
 * Safe to call from print helpers and from component runtime.
 */
export function syncTextareaHeight(el: HTMLTextAreaElement): void {
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

export interface UseTextareaAutoResizeDeps {
  enabled: MaybeRefOrGetter<boolean>
  /** When provided, changes trigger a remeasure after DOM flush. */
  modelValue?: MaybeRefOrGetter<string | undefined>
  /** Row count affects native min-height; remeasure when it changes. */
  rows?: MaybeRefOrGetter<number | undefined>
}

/**
 * Keeps a textarea height in sync with content and width (word wrap), coalesced to one layout pass per frame.
 */
export function useTextareaAutoResize(textareaRef: Ref<HTMLTextAreaElement | null>, deps: UseTextareaAutoResizeDeps): void {
  let raf = 0
  let resizeObserver: ResizeObserver | null = null

  function clearInlineHeight(): void {
    const el = textareaRef.value
    if (!el) return
    el.style.height = ''
  }

  function runSync(): void {
    const el = textareaRef.value
    if (!el || !toValue(deps.enabled)) return
    syncTextareaHeight(el)
  }

  function schedule(): void {
    if (!toValue(deps.enabled)) {
      if (raf) {
        cancelAnimationFrame(raf)
        raf = 0
      }
      return
    }
    if (raf) return
    raf = requestAnimationFrame(() => {
      raf = 0
      runSync()
    })
  }

  function ensureResizeObserver(): void {
    if (!toValue(deps.enabled)) return
    const el = textareaRef.value
    if (!el || resizeObserver || typeof ResizeObserver === 'undefined') return
    resizeObserver = new ResizeObserver(() => schedule())
    resizeObserver.observe(el)
  }

  function teardownObserver(): void {
    resizeObserver?.disconnect()
    resizeObserver = null
  }

  watch(
    () => {
      const parts: unknown[] = [toValue(deps.enabled)]
      if (deps.modelValue !== undefined) parts.push(toValue(deps.modelValue))
      if (deps.rows !== undefined) parts.push(toValue(deps.rows))
      return parts
    },
    () => {
      if (!toValue(deps.enabled)) {
        if (raf) {
          cancelAnimationFrame(raf)
          raf = 0
        }
        teardownObserver()
        clearInlineHeight()
        return
      }
      ensureResizeObserver()
      schedule()
    },
    { flush: 'post' }
  )

  onMounted(() => {
    ensureResizeObserver()
    schedule()
  })

  onBeforeUnmount(() => {
    if (raf) {
      cancelAnimationFrame(raf)
      raf = 0
    }
    teardownObserver()
  })
}
